import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { isEqual } from "lodash";
import { Button, Dropdown, Input, message, Modal, Popconfirm, Tooltip } from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  DownOutlined,
  EditOutlined,
} from "@ant-design/icons";

import { GET_GENERIC_FILTERS, GET_GENERIC_FILTER_TYPES } from "../../../graphql/query";
import {
  CREATE_GENERIC_FILTER,
  DELETE_GENERIC_FILTER,
  UPDATE_GENERIC_FILTER,
} from "../../../graphql/mutation";
import { setAppliedFilterSnapshot } from "../../../redux/slices/leadSlice";
import { getGenericFilterId } from "../../../library/utils";

// Activity Center reuses the generic per-user filter store (parity with Lead
// Center). `getGenericFilterId(types, 2)` resolves the existing
// LEAD_FILTER_ACTIVITY_CENTER type id from getGenericFilterTypes — see
// src/library/utils.js. No backend change needed; the type already exists.
const ACTIVITY_CENTER_MODE = 2;

// Antd `boxShadowSecondary` (Dropdown elevated surface) — matches the design spec.
const DROPDOWN_SHADOW =
  "0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05)";

// The filter fields the panel actually controls. Used to decide whether the
// currently-applied set still matches the saved preset that was applied (see
// `isDirty` below). The redux `filters` object carries extra keys (e.g.
// `search`, derived counts) that are not part of a preset, so we compare only
// this allow-list rather than the whole object.
const COMPARABLE_KEYS = [
  "quickFilters",
  "campaignFilters",
  "userFilters",
  "teamFilters",
  "dateFilterType",
  "dateRange",
  "opportunityTypeFilters",
  "opportunityStatusFilters",
  "opportunitySourceFilters",
];

const pickComparable = f => {
  const picked = {};
  COMPARABLE_KEYS.forEach(key => {
    picked[key] = f?.[key] ?? null;
  });
  return picked;
};

/**
 * Saved-filters footer for the Activity Center filter panel.
 *
 * - "Saved filters" dropdown: disabled while nothing is saved; once the user
 *   has ≥1 saved filter it lists them, applies on click, and offers inline
 *   rename + delete per row.
 * - "Save filter" button: shown only when the applied set is "dirty" — i.e.
 *   there are active filters AND either no saved preset is currently applied or
 *   the user has since changed it. Applying a saved preset on its own does not
 *   surface the button (EAI-1506). Opens a small popup to name and persist the
 *   currently-applied filters.
 *
 * The applied filter set (`filters` = state.lead.filters) is serialized to the
 * `filterJson` string on save, and parsed back + handed to `onApply` when a
 * saved filter is selected.
 */
const SavedFiltersFooter = ({ filters, hasActiveFilters = false, onApply }) => {
  const [savedFilters, setSavedFilters] = useState([]);
  const [listOpen, setListOpen] = useState(false);

  // Save popup
  const [saveOpen, setSaveOpen] = useState(false);
  const [newName, setNewName] = useState("");

  // Inline rename
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");

  const dispatch = useDispatch();

  // Baseline the "dirty" check compares against — the filter set as it was when
  // the user last applied a preset or saved a filter (null while filtering
  // manually). Lives in redux, not local state, so it survives leaving and
  // returning to the Activity Center tab; local state would reset on unmount
  // while the applied `filters` persist, wrongly surfacing the Save button on
  // return (EAI-1506). Set by `applyLeadFilters` on apply and below on save.
  const appliedSnapshot = useSelector(state => state.lead.appliedFilterSnapshot);

  // Id of the row whose delete Tooltip is currently open. Controlled so we can
  // force it closed the instant the delete Popconfirm opens (EAI-1506) —
  // otherwise the hover tooltip lingers over and blocks the confirm UI.
  const [deleteTipId, setDeleteTipId] = useState(null);

  const { data: typesData } = useQuery(GET_GENERIC_FILTER_TYPES);
  const genericFilterTypes = typesData?.getGenericFilterTypes || [];
  const genericFilterTypeId = getGenericFilterId(genericFilterTypes, ACTIVITY_CENTER_MODE);

  const [fetchFilters] = useLazyQuery(GET_GENERIC_FILTERS, { fetchPolicy: "network-only" });
  const [createGenericFilter, { loading: creating }] = useMutation(CREATE_GENERIC_FILTER);
  const [updateGenericFilter] = useMutation(UPDATE_GENERIC_FILTER);
  const [deleteGenericFilter] = useMutation(DELETE_GENERIC_FILTER);

  const loadFilters = async () => {
    if (!genericFilterTypeId) return;
    try {
      const res = await fetchFilters({ variables: { genericFilterTypeId } });
      const all = res?.data?.getGenericFilters || [];
      // Surface ONLY genuine Activity Center saved filters. Our panel always
      // stores the filter set as a serialized object (state.lead.filters).
      // Other/legacy records under this type store an array (e.g. "[]",
      // "[\"MY_CONVERSATIONS\"]") — those are not Activity Center presets, so
      // exclude them rather than list non-Activity-Center filters.
      const activityCenterFilters = all.filter(f => {
        try {
          const parsed = JSON.parse(f?.genericFilterJson?.filterJson || "null");
          return parsed && typeof parsed === "object" && !Array.isArray(parsed);
        } catch {
          return false;
        }
      });
      // Display alphabetically by filter name (case-insensitive) so the list
      // has a stable, predictable order instead of the backend's arbitrary
      // order (EAI-1506).
      const sorted = [...activityCenterFilters].sort((a, b) =>
        (a?.filterName || "").localeCompare(b?.filterName || "", undefined, {
          sensitivity: "base",
        })
      );
      setSavedFilters(sorted);
    } catch (err) {
      console.log("Error loading saved filters", err);
    }
  };

  useEffect(() => {
    loadFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [genericFilterTypeId]);

  // Once every filter is cleared (Clear all clears it in the reducer, or the
  // user unset them manually) there is no longer an applied preset to compare
  // against — drop the snapshot so the next manual filtering session starts
  // clean.
  useEffect(() => {
    if (!hasActiveFilters && appliedSnapshot) dispatch(setAppliedFilterSnapshot(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasActiveFilters]);

  const handleSave = async () => {
    const name = newName.trim();
    if (!name) {
      message.error("Please enter a filter name");
      return;
    }
    if (!genericFilterTypeId) {
      message.error("Saved filters are unavailable right now");
      return;
    }
    try {
      const res = await createGenericFilter({
        variables: {
          filterJson: JSON.stringify(filters),
          filterName: name,
          isCurrent: true,
          genericFilterTypeId,
        },
      });
      if (res?.data?.createGenericFilter?.statusCode === 200) {
        message.success(res?.data?.createGenericFilter?.message || "Filter saved");
        // The just-saved set is now the applied preset — treat it as clean so
        // the Save button hides again until the user changes something.
        dispatch(setAppliedFilterSnapshot(filters));
        setNewName("");
        setSaveOpen(false);
        loadFilters();
      } else {
        message.error(res?.data?.createGenericFilter?.message || "Could not save filter");
      }
    } catch (err) {
      console.log("Error saving filter", err);
      message.error("Something went wrong");
    }
  };

  const handleApply = preset => {
    try {
      const parsed = JSON.parse(preset?.genericFilterJson?.filterJson || "{}");
      // `applyLeadFilters` records the applied set as the clean baseline in
      // redux, so the Save button stays hidden until the user modifies it
      // (EAI-1506).
      onApply?.(parsed);
      setListOpen(false);
    } catch (err) {
      console.log("Error applying saved filter", err);
      message.error("Could not apply this filter");
    }
  };

  const startRename = preset => {
    setEditingId(preset.id);
    setEditingName(preset.filterName);
  };

  const cancelRename = () => {
    setEditingId(null);
    setEditingName("");
  };

  const handleRename = async preset => {
    const name = editingName.trim();
    if (!name) {
      message.error("Please enter a filter name");
      return;
    }
    try {
      const resp = await updateGenericFilter({
        variables: {
          id: preset.id,
          filterName: name,
          // Rename only — keep the stored filter body untouched.
          filterJson: preset?.genericFilterJson?.filterJson,
          isCurrent: preset?.genericFilterJson?.isCurrent,
          genericFilterTypeId,
        },
      });
      message.success(resp?.data?.updateGenericFilter?.message || "Filter renamed");
      cancelRename();
      loadFilters();
    } catch (err) {
      console.log("Error renaming filter", err);
      message.error("Something went wrong");
    }
  };

  const handleDelete = async preset => {
    try {
      const resp = await deleteGenericFilter({ variables: { id: preset.id } });
      message.success(resp?.data?.deleteGenericFilter?.message || "Filter deleted");
      if (editingId === preset.id) cancelRename();
      loadFilters();
    } catch (err) {
      console.log("Error deleting filter", err);
      message.error("Something went wrong");
    }
  };

  const hasSaved = savedFilters.length > 0;

  // "Dirty" = there's something to save that isn't already the applied preset.
  // True when filtering manually (no preset applied) or when the current set
  // has diverged from the preset the user applied. Gates the Save button so
  // that merely applying a saved filter does not surface it (EAI-1506).
  const isDirty =
    hasActiveFilters &&
    (!appliedSnapshot || !isEqual(pickComparable(appliedSnapshot), pickComparable(filters)));

  const closeSave = () => {
    setSaveOpen(false);
    setNewName("");
  };

  const savedListContent = (
    <div
      className="w-[240px] max-h-[280px] overflow-y-auto py-1.5"
      style={{ borderRadius: 8, background: "#FFF", boxShadow: DROPDOWN_SHADOW }}
      onClick={e => e.stopPropagation()}
    >
      {/* Section header (matches design) */}
      <div className="px-4 py-1 text-black-45 text-sm">My filters</div>
      {savedFilters.map(preset => {
        const isEditing = editingId === preset.id;
        return (
          <div
            key={preset.id}
            className="group flex items-center justify-between gap-2 px-4 py-1.5 hover:bg-[#f5f5f5]"
          >
            {isEditing ? (
              <>
                <Input
                  size="small"
                  autoFocus
                  value={editingName}
                  onChange={e => setEditingName(e.target.value)}
                  onPressEnter={() => handleRename(preset)}
                />
                <div className="flex items-center gap-2 shrink-0">
                  <CheckOutlined
                    className="text-twLinkColor cursor-pointer"
                    onClick={() => handleRename(preset)}
                  />
                  <CloseOutlined className="text-black-45 cursor-pointer" onClick={cancelRename} />
                </div>
              </>
            ) : (
              <>
                <span
                  className="flex-auto truncate text-sm text-black-88 cursor-pointer"
                  onClick={() => handleApply(preset)}
                  title={preset.filterName}
                >
                  {preset.filterName}
                </span>
                <div className="flex items-center gap-3 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Tooltip title="Rename">
                    <EditOutlined
                      className="text-black-65 hover:text-black-88 cursor-pointer"
                      onClick={() => startRename(preset)}
                    />
                  </Tooltip>
                  <Popconfirm
                    title="Delete this filter?"
                    okText="Delete"
                    okButtonProps={{ danger: true }}
                    onConfirm={() => handleDelete(preset)}
                    // Close the hover tooltip the moment the confirm opens so it
                    // doesn't linger over and block the delete UI (EAI-1506).
                    onOpenChange={open => {
                      if (open) setDeleteTipId(null);
                    }}
                  >
                    <Tooltip
                      title="Delete"
                      open={deleteTipId === preset.id}
                      onOpenChange={open => setDeleteTipId(open ? preset.id : null)}
                    >
                      <DeleteOutlined className="text-black-65 hover:text-red-500 cursor-pointer" />
                    </Tooltip>
                  </Popconfirm>
                </div>
              </>
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <>
      <div className="flex items-center gap-4">
        {/* Saved filters dropdown — disabled until at least one filter is saved */}
        {hasSaved ? (
          <Dropdown
            open={listOpen}
            onOpenChange={setListOpen}
            trigger={["click"]}
            placement="topLeft"
            dropdownRender={() => savedListContent}
          >
            <span className="flex items-center gap-1.5 text-sm text-black-88 cursor-pointer">
              Saved filters
              <DownOutlined className="text-[10px]" />
            </span>
          </Dropdown>
        ) : (
          <Tooltip title="Apply and save a filter to see it here">
            <span className="flex items-center gap-1.5 text-sm text-gray-300 cursor-not-allowed">
              Saved filters
              <DownOutlined className="text-[10px]" />
            </span>
          </Tooltip>
        )}

        {/* Save filter — only when the applied set is dirty (not just a
            freshly-applied saved filter). */}
        {isDirty && <Button onClick={() => setSaveOpen(true)}>Save filter</Button>}
      </div>

      {/* Save filter form — compact centered card matching the Figma design
          (node 4196-12483): clean white card — title + close, name field, then
          left-aligned Save / Cancel. No section dividers, no footer background.
          Padding comes from the global `.ant-modal-body` rule (10px 24px 20px)
          with `.ant-modal-content` at 0, so there's a single layer of spacing —
          no stacked/dead space (EAI-1506). */}
      <Modal
        open={saveOpen}
        onCancel={closeSave}
        centered
        width={408}
        footer={null}
        closable={false}
      >
        <div>
          {/* Header — full-width bottom divider. Negative margins bleed the
              line to the modal edges past the body's 24px side padding. */}
          <div className="flex items-center justify-between -mx-6 px-6 pb-3 mb-6 border-solid border-0 border-b border-[#f0f0f0]">
            <span className="text-black-88 font-semibold text-base">Save filter</span>
            <CloseOutlined className="text-[#00000073] cursor-pointer" onClick={closeSave} />
          </div>
          <div className="text-sm text-black-88 mb-1.5">Filter name</div>
          <Input
            autoFocus
            placeholder="Name your filter"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            onPressEnter={handleSave}
          />
          <div className="flex gap-2 mt-10">
            <Button type="primary" loading={creating} onClick={handleSave}>
              Save
            </Button>
            <Button onClick={closeSave}>Cancel</Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default SavedFiltersFooter;
